import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Boxes, ChevronDown, ChevronRight, CircleCheck, Hourglass, PackageX, ShieldAlert, UserX,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { postRecoveryApi, type NotRunObject, type NotRunReason } from './api'

/**
 * The objects in a recovery that never ran, and why.
 *
 * An object never runs when there was no good copy to recover from — or when there was one and
 * nobody got to it. Those are not the same failure and they are not fixed by the same person,
 * which is why this is grouped rather than listed. A flat list of thirty objects that did not run
 * is a list nobody can act on; split by reason it is two short lists addressed to the vault team
 * and to whoever staffed the recovery, plus one long list addressed to nobody, because it is not
 * a failure at all.
 *
 * SEVERITY ORDER, NEVER SIZE ORDER. no_runbook is usually the largest group and the least
 * important one; sorted by count it lands first and buries the two rose groups this panel exists
 * for. It goes last, and it starts collapsed behind its count.
 */

type Tone = 'rose' | 'amber' | 'muted'

const TONES: Record<Tone, { tint: string; border: string; text: string; chip: string }> = {
  rose: {
    tint: 'bg-rose-50 dark:bg-rose-950/40',
    border: 'border-rose-200 dark:border-rose-900/60',
    text: 'text-rose-700 dark:text-rose-300',
    chip: 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300',
  },
  amber: {
    tint: 'bg-amber-50 dark:bg-amber-950/40',
    border: 'border-amber-200 dark:border-amber-900/60',
    text: 'text-amber-700 dark:text-amber-300',
    chip: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300',
  },
  /**
   * The theme's own muted tokens rather than a slate ramp. This group is meant to recede in both
   * themes, and a tint that recedes against a light page is not the one that recedes against a
   * dark one — the muted token is already the answer the rest of the product uses.
   */
  muted: {
    tint: 'bg-muted/50',
    border: 'border-border',
    text: 'text-muted-foreground',
    chip: 'bg-muted text-muted-foreground',
  },
}

interface ReasonSpec {
  reason: NotRunReason
  tone: Tone
  Icon: typeof ShieldAlert
  label: string
  /** Who this belongs to. Short, because it is the thing to read first after the count. */
  owner: string
  /** What it means and who fixes it, in one line. */
  meaning: string
  /** Only the vault reasons carry copy evidence worth printing beside the object. */
  copyEvidence: boolean
  /** For the two "nobody started it" reasons, the task count is the size of the work skipped. */
  taskEvidence: boolean
  collapsedByDefault: boolean
}

/** Declaration order IS the render order, and it is severity. Do not sort this by anything. */
const REASONS: ReasonSpec[] = [
  {
    reason: 'all_copies_red',
    tone: 'rose',
    Icon: ShieldAlert,
    label: 'Every copy compromised',
    owner: 'Vault problem',
    meaning: 'Every copy in the chain is compromised, so there was nothing clean to recover from. The vault team owns this — it is a gap in what was protected, not in how the recovery was run.',
    copyEvidence: true,
    taskEvidence: true,
    collapsedByDefault: false,
  },
  {
    reason: 'no_copies',
    tone: 'rose',
    Icon: PackageX,
    label: 'Nothing in the vault',
    owner: 'Vault problem',
    meaning: 'The object has a runbook but nothing at all in the vault. The vault team owns this: it was never being copied in the first place.',
    copyEvidence: true,
    taskEvidence: true,
    collapsedByDefault: false,
  },
  {
    reason: 'not_attempted',
    tone: 'amber',
    Icon: UserX,
    label: 'Never begun',
    owner: 'Resourcing',
    meaning: 'A runbook and a clean copy were both there and the work was never begun. This is a resourcing question for whoever staffed the recovery.',
    copyEvidence: false,
    taskEvidence: true,
    collapsedByDefault: false,
  },
  {
    reason: 'not_started',
    tone: 'amber',
    Icon: Hourglass,
    label: 'Runbook never instantiated',
    owner: 'Recovery lead',
    meaning: 'The runbook was never instantiated for this request — nobody pressed go. The recovery lead owns this.',
    copyEvidence: false,
    taskEvidence: true,
    collapsedByDefault: false,
  },
  {
    reason: 'no_runbook',
    tone: 'muted',
    Icon: Boxes,
    label: 'No runbook of its own',
    owner: 'Not a failure',
    meaning: 'No runbook exists, so this object was never independently recoverable — a container or a service, recovered through its parts. Nothing here needs fixing.',
    copyEvidence: false,
    taskEvidence: false,
    collapsedByDefault: true,
  },
]

const VAULT_REASONS: NotRunReason[] = ['all_copies_red', 'no_copies']
const UNSTARTED_REASONS: NotRunReason[] = ['not_attempted', 'not_started']

/**
 * The reason is a label derived from these two columns; the columns are the evidence it rests on.
 * Where they disagree the row says so out loud rather than being quietly reclassified to match its
 * label — a row tidied into the group it "should" be in is how a vault problem stops being visible.
 */
function evidenceDisagrees(o: NotRunObject): boolean {
  if (o.reason === 'all_copies_red') return o.versionCount === 0 || o.hasCleanChain
  if (o.reason === 'no_copies') return o.versionCount > 0
  return false
}

function Stat({ label, value, detail, tint, tone, icon }: {
  label: string
  value: string
  detail: string
  tint: string
  tone?: string
  icon?: React.ReactNode
}) {
  return (
    <div className={cn('rounded-xl border p-3', tint)}>
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {icon}{label}
      </div>
      <div className={cn('mt-1 text-2xl font-semibold tabular-nums', tone)}>{value}</div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">{detail}</div>
    </div>
  )
}

export function NotRunPanel({ setId }: { setId: number }) {
  const { t } = useTranslation()

  /**
   * The SAME key RunVarianceTable uses, so the two share one read of the set. Which is also why
   * nothing here sets refetchInterval or select: options on a shared key are merged across every
   * observer, and a polling interval added on this panel would silently start polling for the
   * table too.
   */
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['post-recovery-set', setId],
    queryFn: () => postRecoveryApi.setOverview(setId),
  })

  const [collapsed, setCollapsed] = useState<Set<string>>(
    () => new Set(REASONS.filter((r) => r.collapsedByDefault).map((r) => r.reason)),
  )
  const toggle = (key: string) => setCollapsed((prev) => {
    const next = new Set(prev)
    if (!next.delete(key)) next.add(key)
    return next
  })

  const notRun = data?.notRun

  const { groups, unrecognised } = useMemo(() => {
    // ONE ROW PER OBJECT, NOT PER REQUEST. A recovery batch holds a request row per object per
    // main node, so an object beneath two mains appears twice and would be counted twice in
    // every heading. Keeping the first occurrence is safe because the reason is a property of the
    // object and its copies, not of which request asked for it.
    const seen = new Set<string>()
    const rows = (notRun ?? []).filter((o) => {
      const key = o.objectId ?? `#${o.recoverRequestsId}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    const byReason = new Map<string, NotRunObject[]>()
    for (const o of rows) {
      const list = byReason.get(o.reason)
      if (list) list.push(o)
      else byReason.set(o.reason, [o])
    }
    // Anything the client does not know about is still SHOWN, under its raw code. A reason added
    // server-side would otherwise vanish from a panel whose only job is to account for every
    // object that did not run — the counts below have to add up to notRun.length.
    const known = new Set<string>(REASONS.map((r) => r.reason))
    return {
      groups: REASONS
        .map((spec) => ({ spec, objects: byReason.get(spec.reason) ?? [] }))
        .filter((g) => g.objects.length > 0),
      unrecognised: [...byReason.entries()].filter(([reason]) => !known.has(reason)),
    }
  }, [notRun])

  // A failed read is not an empty set. Reporting "every object was recovered" because the request
  // fell over would be the worst thing this panel could say, so the error state comes first.
  if (isError) {
    return (
      <div className={cn('rounded-xl border p-4', TONES.rose.border, TONES.rose.tint)}>
        <p className={cn('text-sm font-medium', TONES.rose.text)}>
          {t('postRecovery.notRunErrorTitle', 'Could not read this recovery set')}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('postRecovery.notRunErrorBody', 'Nothing is being claimed about what did or did not run until this loads. {{detail}}', {
            detail: error instanceof Error ? error.message : '',
          })}
        </p>
      </div>
    )
  }

  if (isLoading || !data) {
    return (
      <div className="rounded-xl border p-6 text-sm text-muted-foreground">
        {t('common.loading', 'Loading…')}
      </div>
    )
  }

  // Counted off the DEDUPED groups, so every heading on this screen adds up to the same total.
  const deduped = [...groups.flatMap((g) => g.objects), ...unrecognised.flatMap(([, o]) => o)]
  const total = deduped.length
  const ran = data.totals?.runCount ?? data.runs.length
  const vaultCount = deduped.filter((o) => VAULT_REASONS.includes(o.reason)).length
  const unstartedCount = deduped.filter((o) => UNSTARTED_REASONS.includes(o.reason)).length
  const noRunbookCount = deduped.filter((o) => o.reason === 'no_runbook').length
  const unknownCount = unrecognised.reduce((a, [, objects]) => a + objects.length, 0)

  /**
   * Empty here is a RESULT, not a missing table. Every object that could run, ran — that is the
   * outcome the whole set was for, and it should read like one rather than like a screen with
   * nothing on it.
   */
  if (total === 0) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900/60 dark:bg-emerald-950/40">
        <div className="flex items-start gap-2.5">
          <CircleCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
          <div>
            <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">
              {t('postRecovery.notRunNoneTitle', 'Every object in this set was recovered')}
            </h3>
            <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
              {t('postRecovery.notRunNoneBody', 'All {{n}} objects ran. None was held back for want of a clean copy in the vault, and none was left unattempted.', { n: ran })}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">{t('postRecovery.notRunTitle', 'Objects that never ran')}</h2>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          {t('postRecovery.notRunIntro', '{{ran}} objects ran in this set and {{n}} did not. An object never runs when there was no good copy to recover from, or when there was one and the work was never begun — the two are grouped separately because different people fix them.', { ran, n: total })}
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Stat
          label={t('postRecovery.notRunStatVault', 'Nothing clean to recover from')}
          value={String(vaultCount)}
          tone={vaultCount ? TONES.rose.text : undefined}
          detail={t('postRecovery.notRunStatVaultDetail', 'a vault problem — the copy was missing or compromised')}
          tint="bg-rose-50 dark:bg-rose-950/40"
          icon={<ShieldAlert className="h-3.5 w-3.5" />}
        />
        <Stat
          label={t('postRecovery.notRunStatUnstarted', 'Ready, never begun')}
          value={String(unstartedCount)}
          tone={unstartedCount ? TONES.amber.text : undefined}
          detail={t('postRecovery.notRunStatUnstartedDetail', 'a resourcing problem — nothing was stopping these')}
          tint="bg-amber-50 dark:bg-amber-950/40"
          icon={<UserX className="h-3.5 w-3.5" />}
        />
        <Stat
          label={t('postRecovery.notRunStatNoRunbook', 'Not independently recoverable')}
          value={String(noRunbookCount)}
          detail={t('postRecovery.notRunStatNoRunbookDetail', 'no runbook of their own — recovered through their parts')}
          tint="bg-muted/50"
          icon={<Boxes className="h-3.5 w-3.5" />}
        />
      </div>

      {/* Worth saying explicitly, because the long collapsed group at the bottom otherwise reads
          as a pile of failures. If nothing is rose or amber, nothing failed. */}
      {vaultCount === 0 && unstartedCount === 0 && unknownCount === 0 && (
        <div className="flex items-start gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900/60 dark:bg-emerald-950/40">
          <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
          <p className="text-xs text-muted-foreground">
            <span className="font-medium text-emerald-800 dark:text-emerald-300">
              {t('postRecovery.notRunNoFailuresTitle', 'Nothing failed to run.')}
            </span>{' '}
            {t('postRecovery.notRunNoFailuresBody', 'Every object that was independently recoverable ran. The {{n}} below have no runbook of their own and were never expected to.', { n: noRunbookCount })}
          </p>
        </div>
      )}

      <div className="space-y-3">
        {groups.map(({ spec, objects }) => (
          <Group
            key={spec.reason}
            tone={TONES[spec.tone]}
            Icon={spec.Icon}
            label={t(`postRecovery.notRunLabel_${spec.reason}`, spec.label)}
            owner={t(`postRecovery.notRunOwner_${spec.reason}`, spec.owner)}
            meaning={t(`postRecovery.notRunMeaning_${spec.reason}`, spec.meaning)}
            objects={objects}
            copyEvidence={spec.copyEvidence}
            taskEvidence={spec.taskEvidence}
            open={!collapsed.has(spec.reason)}
            onToggle={() => toggle(spec.reason)}
          />
        ))}

        {unrecognised.map(([reason, objects]) => (
          <Group
            key={reason}
            tone={TONES.muted}
            Icon={Boxes}
            label={t('postRecovery.notRunUnknownLabel', 'Reason not recognised: {{reason}}', { reason })}
            owner={t('postRecovery.notRunUnknownOwner', 'Unclassified')}
            meaning={t('postRecovery.notRunUnknownMeaning', 'The server recorded a reason this screen does not know how to explain. The objects are listed so they are still accounted for.')}
            objects={objects}
            copyEvidence
            taskEvidence
            open={!collapsed.has(reason)}
            onToggle={() => toggle(reason)}
          />
        ))}
      </div>

      {/* The counts have to reconcile, and saying so is cheaper than someone adding them up. */}
      <p className="text-[11px] text-muted-foreground">
        {t('postRecovery.notRunFooter', '{{n}} objects did not run, out of {{all}} in the set.', { n: total, all: ran + total })}
      </p>
    </div>
  )
}

function Group({
  tone, Icon, label, owner, meaning, objects, copyEvidence, taskEvidence, open, onToggle,
}: {
  tone: { tint: string; border: string; text: string; chip: string }
  Icon: typeof ShieldAlert
  label: string
  owner: string
  meaning: string
  objects: NotRunObject[]
  copyEvidence: boolean
  taskEvidence: boolean
  open: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  const Chevron = open ? ChevronDown : ChevronRight

  return (
    <section className={cn('overflow-hidden rounded-xl border', tone.border)}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={cn('flex w-full items-start gap-2 px-3 py-2.5 text-left', tone.tint)}
      >
        <Chevron className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', tone.text)} aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <h3 className={cn('text-sm font-semibold', tone.text)}>{label}</h3>
            <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium tabular-nums', tone.chip)}>
              {objects.length}
            </span>
            <span className="rounded-full bg-background/70 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {owner}
            </span>
          </div>
          <p className="mt-1 max-w-3xl text-xs text-muted-foreground">{meaning}</p>
        </div>
      </button>

      {open && (
        // Wide content scrolls inside its own box; the page underneath must not move sideways.
        <div className="overflow-x-auto border-t">
          <table className={cn('w-full text-sm', copyEvidence ? 'min-w-[720px]' : 'min-w-[460px]')}>
            <thead>
              <tr className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">{t('postRecovery.notRunColObject', 'Object')}</th>
                <th className="px-3 py-2 font-medium">{t('postRecovery.notRunColDescription', 'Description')}</th>
                {copyEvidence && (
                  <th className="px-3 py-2 font-medium">{t('postRecovery.notRunColCopies', 'Copies in vault')}</th>
                )}
                {copyEvidence && (
                  <th className="px-3 py-2 font-medium">{t('postRecovery.notRunColChain', 'Clean chain')}</th>
                )}
                {taskEvidence && (
                  <th className="px-3 py-2 font-medium">{t('postRecovery.notRunColTasks', 'Runbook tasks')}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {objects.map((o, i) => (
                <tr key={`${o.recoverRequestsId}-${o.objectId ?? i}`} className="border-b last:border-0">
                  <td className="whitespace-nowrap px-3 py-2 font-medium">
                    {o.objectId ?? t('postRecovery.notRunNoObjectId', 'request {{id}}', { id: o.recoverRequestsId })}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {o.description || t('postRecovery.notRunNoDescription', 'No description recorded')}
                  </td>
                  {copyEvidence && (
                    <td className="px-3 py-2 tabular-nums">{o.versionCount}</td>
                  )}
                  {copyEvidence && (
                    <td className="px-3 py-2">
                      <span className={cn(
                        'rounded px-1.5 py-0.5 text-[11px]',
                        o.hasCleanChain
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                          : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300',
                      )}>
                        {o.hasCleanChain
                          ? t('postRecovery.notRunChainYes', 'clean chain exists')
                          : t('postRecovery.notRunChainNo', 'none')}
                      </span>
                      {evidenceDisagrees(o) && (
                        <span className="ml-2 text-[11px] text-muted-foreground">
                          {t('postRecovery.notRunEvidenceOdd', 'evidence does not match this reason')}
                        </span>
                      )}
                    </td>
                  )}
                  {taskEvidence && (
                    <td className="px-3 py-2 tabular-nums">{o.runbookTasks}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
